import { STATUS_TONES } from '../../../components/Legend';
import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchAnalytics, type AnalyticsResponse } from '../../../api/dashboardApi';
import { SERIES, axis as chartAxis } from '../../../design-system/chartColors';

/**
 * Proper KPIs (SRS #23/#24): regulatory activity in the account's scope,
 * status mix, jurisdictions and types — and usage/adoption for the account
 * or, for an enterprise owner, the team. Every number comes from the
 * scoped analytics endpoint; nothing here is illustrative.
 */

interface Props {
  token: string | null;
  theme: string;
}

/* recharts takes colours as props, so these cannot be tokens. They come from
   the one shared palette instead — a literal here is how the bars stayed
   indigo next to an orange donut when KN's brand was applied. */
const COLORS = SERIES;
// SRS #20: shared status vocabulary (components/Legend.tsx)
const STATUS_COLORS: Record<string, string> = { enforced: STATUS_TONES.in_force.color, pending: STATUS_TONES.pending.color, inactive: STATUS_TONES.inactive.color, unknown: STATUS_TONES.unknown.color };
const STATUS_LABEL: Record<string, string> = { enforced: STATUS_TONES.in_force.label, pending: STATUS_TONES.pending.label, inactive: STATUS_TONES.inactive.label, unknown: STATUS_TONES.unknown.label };

const AnalyticsPanel: React.FC<Props> = ({ token, theme }) => {
  const isDark = theme === 'dark';
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchAnalytics(token).then((d) => { if (!cancelled) setData(d); }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load analytics'); });
    return () => { cancelled = true; };
  }, [token]);

  const { text, muted, grid: border } = chartAxis(isDark);
  const card: React.CSSProperties = { background: isDark ? '#101828' : 'var(--ds-surface)', border: `1px solid ${border}`, borderRadius: 12, padding: '14px 16px' };
  const h3: React.CSSProperties = { margin: '0 0 2px', fontSize: 13.5, fontWeight: 600, color: text };
  const sub: React.CSSProperties = { margin: '0 0 10px', fontSize: 12, color: muted };

  if (error) return <p style={{ color: muted, fontStyle: 'italic' }}>{error}</p>;
  if (!data) return <p style={{ color: muted, fontStyle: 'italic' }}>Loading analytics…</p>;

  // Nothing in scope: say so once, rather than drawing five empty chart frames.
  //
  // Since the jurisdiction cap started being enforced, an account that has not
  // chosen yet has an empty scope — and this panel rendered a blank donut, a
  // blank axis and a flat line, which reads as a broken product rather than an
  // empty one. The coverage panel below carries the full explanation and the
  // link; this says enough not to alarm anyone on the way past.
  if (data.scope.entitled_jurisdictions !== null && data.scope.entitled_jurisdictions.length === 0) {
    return (
      <div style={{ ...card, display: 'grid', gap: 6 }} data-testid="analysis-no-scope">
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: text }}>Analysis</h2>
        <p style={{ margin: 0, fontSize: 13, color: muted }}>
          There is nothing to chart until your scope has some jurisdictions in it. Choose them below and
          the figures, the activity over time and the status mix all fill in.
        </p>
      </div>
    );
  }

  const scopeLabel = data.scope.entitled_jurisdictions === null ? 'all jurisdictions' : data.scope.entitled_jurisdictions.length ? data.scope.entitled_jurisdictions.join(', ') : 'no jurisdictions';
  const statusData = Object.entries(data.regulatory.status_mix).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, key: k, value: v }));
  const last12 = data.regulatory.months;
  const actions12 = last12.reduce((s, m) => s + m.legislative_actions, 0);
  const tracked12 = last12.reduce((s, m) => s + m.newly_tracked, 0);
  const usage = data.usage;
  const usageTotals = usage.months.reduce((a, m) => ({ chats: a.chats + m.chats, assessments: a.assessments + m.assessments, logins: a.logins + m.logins }), { chats: 0, assessments: 0, logins: 0 });

  const kpi = (label: string, value: string | number, hint: string) => (
    <div style={{ ...card, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: muted }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: text, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: muted, marginTop: 2 }}>{hint}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: text }}>Analysis — {scopeLabel}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: muted }}>Regulatory activity in your scope over the last 12 months, and how your {usage.scope === 'team' ? 'team' : 'account'} is using the platform.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {kpi('Regulations in scope', data.regulatory.total.toLocaleString(), `${statusData.find((s) => s.key === 'enforced')?.value ?? 0} in force`)}
        {kpi('Legislative actions', actions12.toLocaleString(), 'last 12 months')}
        {kpi('Newly tracked', tracked12.toLocaleString(), 'added to the platform, last 12 months')}
        {usage.scope !== 'none' && kpi(usage.scope === 'team' ? 'Team activity' : 'Your activity', (usageTotals.chats + usageTotals.assessments).toLocaleString(), `${usageTotals.chats} chats · ${usageTotals.assessments} assessments, 6 months`)}
        {usage.scope === 'team' && kpi('Active members', `${usage.active_members_30d ?? 0}/${usage.total_members ?? 0}`, 'signed in during the last 30 days')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 14 }}>
        <div style={card}>
          <h3 style={h3}>Regulatory activity</h3>
          <p style={sub}>Legislative actions and newly tracked regulations per month</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={last12}>
              <CartesianGrid stroke={border} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: muted }} />
              <YAxis tick={{ fontSize: 11, fill: muted }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="legislative_actions" name="Legislative actions" stroke={SERIES[0]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="newly_tracked" name="Newly tracked" stroke={SERIES[1]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <h3 style={h3}>Status mix</h3>
          <p style={sub}>Where the regulations in your scope stand</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {statusData.map((s) => <Cell key={s.key} fill={STATUS_COLORS[s.key] || '#d0d5dd'} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <h3 style={h3}>By jurisdiction</h3>
          <p style={sub}>Total tracked, and how many had activity in the last 90 days</p>
          <ResponsiveContainer width="100%" height={Math.max(160, 26 * data.regulatory.by_jurisdiction.length + 40)}>
            <BarChart data={data.regulatory.by_jurisdiction} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid stroke={border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: muted }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: muted }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total" name="Tracked" fill={SERIES[0]} radius={[0, 4, 4, 0]} />
              <Bar dataKey="active_90d" name="Active (90d)" fill={SERIES[1]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <h3 style={h3}>By type</h3>
          <p style={sub}>Instrument types in your scope</p>
          <ResponsiveContainer width="100%" height={Math.max(160, 26 * data.regulatory.by_type.length + 40)}>
            <BarChart data={data.regulatory.by_type} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid stroke={border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: muted }} allowDecimals={false} />
              <YAxis type="category" dataKey="type" width={130} tick={{ fontSize: 11, fill: muted }} />
              <Tooltip />
              <Bar dataKey="n" name="Regulations" radius={[0, 4, 4, 0]}>
                {data.regulatory.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {usage.scope !== 'none' && (
          <div style={card}>
            <h3 style={h3}>{usage.scope === 'team' ? 'Team usage' : 'Your usage'}</h3>
            <p style={sub}>Chats, assessments and sign-ins per month</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={usage.months}>
                <CartesianGrid stroke={border} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: muted }} />
                <YAxis tick={{ fontSize: 11, fill: muted }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="chats" name="Chats" fill={SERIES[0]} stackId="a" />
                <Bar dataKey="assessments" name="Assessments" fill={SERIES[1]} stackId="a" />
                <Bar dataKey="logins" name="Sign-ins" fill={SERIES[6]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {usage.scope === 'team' && usage.members && (
          <div style={card}>
            <h3 style={h3}>Adoption by member</h3>
            <p style={sub}>Last 30 days</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr>{['Member', 'Chats', 'Assessments', 'Sign-ins'].map((h) => <th key={h} style={{ textAlign: h === 'Member' ? 'left' : 'right', padding: '4px 6px', color: muted, fontWeight: 500, borderBottom: `1px solid ${border}` }}>{h}</th>)}</tr></thead>
              <tbody>{usage.members.map((m) => (
                <tr key={m.user_id}>
                  <td style={{ padding: '5px 6px', color: text }}>{m.name}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', color: text }}>{m.chats_30d}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', color: text }}>{m.assessments_30d}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', color: m.logins_30d ? text : '#b42318' }}>{m.logins_30d || 'none'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPanel;
